/**
 * AlwaysExplainer — explainer for the ALWAYS Structural Duality Principle (Al).
 *
 * ALWAYS is the level-1 SDP that pairs with INV.CONSISTENCY along the
 * F-algebra ⊣ F-coalgebra adjunction. It certifies that establishment and
 * preservation compose safely — that an INV.CONSISTENCY witness can be
 * stacked through arbitrary refactorings without producing forbidden
 * patterns.
 *
 * Together, INV.CONSISTENCY (primal) and ALWAYS (dual) form the safety
 * envelope ICG-Al — a single two-sided certificate that closes the audit
 * surface.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CalloutBox,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-always",
  kind: "SSM · Compositionality Principle (S2 · level 1)",
  title: "ALWAYS",
  subtitle: "Al · F-algebra ↔ F-coalgebra · Neutralization Theorems: I1 + I2",
  accent: ACCENT,
  accentAlt: DUAL_PAIR_COLOR["INV.CONSISTENCY"].color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "INV.CONSISTENCY (primal)",
    href: "#explainer-inv-consistency",
    accent: DUAL_PAIR_COLOR["INV.CONSISTENCY"].color,
  },
  {
    label: "INV.ESTABLISH",
    href: "#explainer-inv-establish",
    accent: THEOREM_COLOR["INV.ESTABLISH"],
  },
  { label: "INV.PRESERVE", href: "#explainer-inv-preserve", accent: THEOREM_COLOR["INV.PRESERVE"] },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ALWAYS</strong> says that if two invariants each hold step-by-step, their stack stays
      safe by construction.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "the invariant always holds" in a single piece of code, you immediately
        want to stack two of them. ALWAYS is the certificate that says "stacking is safe" — that the
        joint invariant is itself an INV.CONSISTENCY witness, not a weaker one.
      </p>
      <p>
        Where INV.CONSISTENCY delivers the guarantee inside one regime, ALWAYS delivers
        compositionality across regimes. Together they close as the ICG-Al safety envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ALWAYS is the Structural Duality Principle that pairs F-algebra and F-coalgebra at the SDP
        level. Concretely:
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        At the SDP level the adjunction is <strong>graded</strong> rather than binary (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>): the unit of the
        free-F-algebra ⊣ cofree-F-coalgebra adjunction supplies, at each grade, the precise
        governance datum a bridge must produce to cross the ALWAYS seam. The 1-categorical seam of
        INV.CONSISTENCY says "establishment without preservation is unconstructable"; the SDP
        adjunction says "and the only way to compose two such envelopes is the unit of this
        adjunction."
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        ALWAYS is what lets the ICG-Al safety envelope reduce the audit surface from "two checks"
        (one INV.CONSISTENCY witness per regime) to "one envelope" (one INV.CONSISTENCY witness
        composed through ALWAYS).
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If two invariants stack but the joint envelope is
        not an INV.CONSISTENCY witness, ALWAYS rules it out structurally — there is no
        graded-adjunction unit certifying the stack. The forbidden composition is{" "}
        <em>structurally inexpressible</em> at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Al" accent={ACCENT}>
        INV.CONSISTENCY (DPG) ⊗ ALWAYS (SDP) = ICG-Al, the single two-sided certificate. Audit
        surface collapses from <em>(DPG witness) + (SDP witness)</em> to <em>one envelope</em>{" "}
        carrying the adjunction unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "Joint Neutralization (Agda)",
      path: "agda/Kernel/INV/CONSISTENCY/Neutralization.agda",
    },
    {
      label: "Manuscript chapters (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 05 (ALWAYS SDP) and Chapter 06 (Safety Envelope ICG-Al) land with PR #1544.",
    },
  ],
};

const DIAGRAM = (
  <AdjunctionDiagram
    leftCat="F-Alg"
    rightCat="F-Coalg"
    leftAdjoint="Free_F"
    rightAdjoint="Cofree_F"
    accent={ACCENT}
  />
);

export function AlwaysExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
